import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
  List,
  Grid,
} from '@mui/material';
import {
  Upload,
  ArrowUpDown,
} from 'lucide-react';
import { gql, useMutation } from '@apollo/client';
import { GET_DOCUMENTS } from './UploadDocumentsPage'; // TODO: Verify path
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import { NotificationToaster } from '../../utils/NotificationToaster';
import DocumentListItem from './DocumentListItem'; // Import the list item component
import { FileUpload } from './FileUpload';

const CREATE_DOCUMENT = gql`
  mutation CreateDocument($file: Upload!, $status: String!, $properties: JSON, $size: Int!) {
    createDocument(file: $file, status: $status, properties: $properties, size: $size) {
      id
      name
      userId
      uploadedAt
      status
      size
      type
      summary
      properties
    }
  }
`;

const DELETE_DOCUMENT = gql`
  mutation DeleteDocument($id: ID!) {
    deleteDocument(id: $id) {
      id
    }
  }
`;

const DocumentManagementPanel = ({
  documents = [],
  sortBy,
  isDragging,
  selectedDocId,
  onSelectDocument,
  setNeedsPolling,
  onSortChange,
}) => {
  // --- State ---
  const [localExpandedDocs, setLocalExpandedDocs] = useState(new Set());
  const [docToDelete, setDocToDelete] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [approvedDocs, setApprovedDocs] = useState(new Set());

  // --- Mutations ---
  const [createDocMutation, { loading: createLoading, error: createError }] = useMutation(
    CREATE_DOCUMENT,
    {
      onError: (error) => {
        console.error('DMP: Error creating document mutation:', error);
        NotificationToaster({ message: `Error uploading document: ${error.message}`, type: 'error' });
        setNeedsPolling(false);
      },
      refetchQueries: [{ query: GET_DOCUMENTS }],
    }
  );

  const [deleteDocMutation] = useMutation(DELETE_DOCUMENT, {
    onCompleted: () => {
      NotificationToaster({ message: 'Document deleted successfully', type: 'success' });
    },
    onError: (error) => {
      console.error('Error deleting document:', error);
      NotificationToaster({ message: `Error deleting document: ${error.message}`, type: 'error' });
    },
    refetchQueries: [{ query: GET_DOCUMENTS }],
  });

  // --- File Upload Handler ---
  const handleFileUpload = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) {
      console.log("DMP: No files provided for upload.");
      return;
    }
    console.log(`DMP: Starting upload for ${acceptedFiles.length} files.`);
    setNeedsPolling(true); // Start polling immediately

    // Sequentially upload files - consider parallel uploads if backend supports and it's needed
    for (const file of acceptedFiles) {
      try {
        console.log(`DMP: Uploading ${file.name}...`);
        await createDocMutation({
          variables: {
            file: file,
            status: 'PROCESSING', // Set initial status
            properties: { },
            size: file.size,
          },
        });
        // Success notification is handled by useMutation's onCompleted if needed
      } catch (err) {
        // Error logging/notification is handled by useMutation's onError
        console.error(`DMP: Error during createDocMutation for ${file.name}:`, err);
        // Stop further uploads in this batch if one fails?
        break; // Example: Stop processing batch on first error
      }
    }
    // Polling continues based on setNeedsPolling(true) or stops via mutation's onError
  }, [createDocMutation, setNeedsPolling]);

  // --- Callbacks ---
  const handleToggleExpand = useCallback((docId) => {
    setLocalExpandedDocs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  }, []);

  const handleRequestDelete = useCallback((doc) => {
    setDocToDelete(doc);
    setIsConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (docToDelete) {
      try {
        await deleteDocMutation({ variables: { id: docToDelete.id } });
      } catch (err) {
        console.error('DMP: Caught error during delete confirmation:', err);
      }
      setIsConfirmOpen(false);
      setDocToDelete(null);
    }
  }, [docToDelete, deleteDocMutation]);

  const handleCancelDelete = useCallback(() => {
    setIsConfirmOpen(false);
    setDocToDelete(null);
  }, []);

  const sortedDocuments = documents;

  const handleApproveDoc = (docId) => {
    setApprovedDocs((prev) => new Set(prev).add(docId));
    // TODO: Add mutation/API call to backend to persist approval
  };

  const handleRejectDoc = (docId) => {
    setApprovedDocs((prev) => {
      const newSet = new Set(prev);
      newSet.delete(docId);
      return newSet;
    });
    // TODO: Add mutation/API call to backend to persist rejection
  };

  return (
    <Grid container spacing={3}>
      {documents.length === 0 ? (
        <Grid item xs={12}>
          <FileUpload
            createDocMutation={createDocMutation}
            onFileUpload={handleFileUpload} // Pass the handler
            processingFiles={[]} // Explicitly pass processingFiles={[]} to the FileUpload component
            setNeedsPolling={setNeedsPolling}
            uploadLoading={createLoading}
            uploadError={createError}
          />
        </Grid>
      ) : (
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 0, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" component="h2">
                Uploaded Documents ({documents.length})
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {createLoading && <CircularProgress size={24} sx={{ mr: 1 }} />}
                <Button
                  size="small"
                  onClick={onSortChange}
                  startIcon={<ArrowUpDown size={16} />}
                  disabled={createLoading}
                >
                  Sort by {sortBy === 'date' ? 'Name' : 'Date'}
                </Button>
                <input
                  accept="*/*"
                  style={{ display: 'none' }}
                  id="append-button-file"
                  multiple
                  type="file"
                  onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                  disabled={createLoading}
                />
                <label htmlFor="append-button-file">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<Upload size={18} />}
                    sx={{ ml: 1 }}
                    disabled={createLoading}
                  >
                    Add More Files
                  </Button>
                </label>
              </Box>
            </Box>
            <List sx={{ p: 0 }} disablePadding>
              {sortedDocuments.map((doc) => (
                <DocumentListItem
                  key={doc.id} // Key must be on the top-level element in map
                  doc={doc}
                  selectedDocId={selectedDocId}
                  approvedDocs={approvedDocs}
                  localExpandedDocs={localExpandedDocs}
                  onSelectDocument={onSelectDocument}
                  handleApproveDoc={handleApproveDoc}
                  handleRejectDoc={handleRejectDoc}
                  handleToggleExpand={handleToggleExpand}
                  handleRequestDelete={handleRequestDelete}
                />
              ))}
            </List>
          </Paper>
        </Grid>
      )}
      {/* Use the extracted ConfirmDeleteDialog component */}
      <ConfirmDeleteDialog
        open={isConfirmOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        docName={docToDelete ? docToDelete.name : ''}
      />
    </Grid>
  );
};

export default DocumentManagementPanel;
